import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Heart, Coffee, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESET_AMOUNTS = [
  { value: 500, label: "$5", emoji: "☕" },
  { value: 1000, label: "$10", emoji: "🍕" },
  { value: 2500, label: "$25", emoji: "💪" },
  { value: 5000, label: "$50", emoji: "🔥" },
];

export default function DonateButton({ variant = "default" }) {
  const [showModal, setShowModal] = useState(false);
  const [amount, setAmount] = useState(1000);
  const [customAmount, setCustomAmount] = useState("");
  const [loading, setLoading] = useState(false);

  const handleDonate = async () => {
    const donationAmount = customAmount ? parseInt(customAmount) * 100 : amount;
    
    if (donationAmount < 100) {
      toast.error("Minimum donation is $1");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/payments/donate`, {
        amount: donationAmount,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error("Failed to process donation");
      setLoading(false);
    }
  };

  if (variant === "minimal") {
    return (
      <Button
        onClick={() => setShowModal(true)}
        variant="outline"
        className="gap-2 border-pink-500/30 text-pink-400 hover:bg-pink-500/10"
      >
        <Heart className="h-4 w-4" /> Support
      </Button>
    );
  }

  return (
    <>
      <Button
        data-testid="donate-btn"
        onClick={() => setShowModal(true)}
        variant="outline"
        className="gap-2 border-pink-500/30 text-pink-400 hover:bg-pink-500/10"
      >
        <Heart className="h-4 w-4" /> Support
      </Button>

      {/* Donate Modal */}
      {showModal && (
        <div className="fixed inset-0 z-[100] bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md"
          >
            <Card className="bg-card border-pink-500/30">
              <CardContent className="p-6">
                {/* Header */}
                <div className="text-center mb-6">
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className="w-16 h-16 mx-auto mb-4 rounded-full bg-pink-500/20 flex items-center justify-center"
                  >
                    <Heart className="h-8 w-8 text-pink-500" />
                  </motion.div>
                  <h2 className="text-2xl font-black uppercase tracking-tighter">
                    Support Coach Coop
                  </h2>
                  <p className="text-muted-foreground text-sm mt-1">
                    Your donation helps us keep the platform running
                  </p>
                </div>

                {/* Preset Amounts */}
                <div className="grid grid-cols-4 gap-2 mb-4">
                  {PRESET_AMOUNTS.map((preset) => (
                    <button
                      key={preset.value}
                      onClick={() => {
                        setAmount(preset.value);
                        setCustomAmount("");
                      }}
                      className={`p-3 rounded-lg text-center transition-all ${
                        amount === preset.value && !customAmount
                          ? "bg-pink-500 text-white"
                          : "bg-secondary hover:bg-secondary/80"
                      }`}
                    >
                      <div className="text-lg">{preset.emoji}</div>
                      <div className="font-bold text-sm">{preset.label}</div>
                    </button>
                  ))}
                </div>

                {/* Custom Amount */}
                <div className="mb-6">
                  <label className="text-xs text-muted-foreground uppercase tracking-wider">
                    Custom Amount
                  </label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <Input
                      type="number"
                      placeholder="Enter amount"
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      className="pl-7 bg-secondary border-transparent"
                    />
                  </div>
                </div>

                {/* Donate Button */}
                <Button
                  data-testid="confirm-donate-btn"
                  onClick={handleDonate}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-pink-500 to-red-500 hover:from-pink-600 hover:to-red-600 py-6 uppercase font-bold tracking-widest"
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      <Heart className="mr-2 h-5 w-5" />
                      Donate ${customAmount || amount / 100}
                    </>
                  )}
                </Button>

                {/* Cancel */}
                <button
                  onClick={() => setShowModal(false)}
                  className="w-full mt-3 text-sm text-muted-foreground hover:text-foreground"
                >
                  Cancel
                </button>

                {/* Secure badge */}
                <p className="mt-4 text-xs text-center text-muted-foreground">
                  🔒 Secure payment via Stripe
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      )}
    </>
  );
}
